import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions, Avatar, Chip,
  Button, IconButton, TextField, InputAdornment, Tabs, Tab, Paper,
  Divider, Stack, CircularProgress, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Search, PersonAdd, PersonRemove, Check, Close, People, HourglassTop,
  PersonSearch, ScubaDiving,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import InviteButton from './InviteButton';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import IncomingSharesPanel from './IncomingSharesPanel';
import { useBuddies } from '../hooks/useBuddies';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import { buddyService, UserPublicProfile } from '../services/buddyService';

// ── Helpers ─────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

function BuddyAvatar({ name, size = 48 }: { name: string; size?: number }) {
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: 'primary.main', fontWeight: 700, fontSize: size * 0.35 }}>
      {initials(name) || '?'}
    </Avatar>
  );
}

// ── My Buddies panel ─────────────────────────────────────────────────────────

function MyBuddiesPanel({
  buddies,
  onRemove,
  onOpenProfile,
  loadingId,
  profiles,
}: {
  buddies: { userId: string; buddyUserId: string }[];
  onRemove: (buddyUserId: string) => void;
  onOpenProfile: (userId: string) => void;
  loadingId: string | null;
  profiles: Record<string, UserPublicProfile>;
}) {
  if (buddies.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <People sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          No dive buddies yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the Find tab to search and add buddies.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      {buddies.map(b => {
        const profile = profiles[b.buddyUserId];
        const displayName = profile?.displayName ?? b.buddyUserId;
        return (
          <Grid item xs={12} sm={6} md={12} key={b.buddyUserId}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{ flexGrow: 1, cursor: 'pointer' }}
                onClick={() => onOpenProfile(b.buddyUserId)}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <BuddyAvatar name={displayName} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflowWrap: 'anywhere',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {displayName}
                    </Typography>
                    {profile && (
                      <Typography variant="caption" color="text.secondary">
                        {profile.diveCountLabel}
                        {profile.lastDiveDate && ` · last ${new Date(profile.lastDiveDate).toLocaleDateString()}`}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
              <Divider />
              <CardActions sx={{ px: 1.5, py: 0.75, justifyContent: 'flex-end' }}>
                <Tooltip title="Remove buddy">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemove(b.buddyUserId)}
                    disabled={loadingId === b.buddyUserId}
                    sx={{ minHeight: 44, minWidth: 44 }}
                  >
                    {loadingId === b.buddyUserId
                      ? <CircularProgress size={18} />
                      : <PersonRemove fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

// ── Pending panel ────────────────────────────────────────────────────────────

function PendingPanel({
  incoming,
  outgoing,
  onRespond,
  onCancel,
  loadingId,
  profiles,
}: {
  incoming: { buddyUserId: string; initiatorUserId: string }[];
  outgoing: { buddyUserId: string; initiatorUserId: string }[];
  onRespond: (buddyUserId: string, accept: boolean) => void;
  onCancel: (buddyUserId: string) => void;
  loadingId: string | null;
  profiles: Record<string, UserPublicProfile>;
}) {
  const empty = incoming.length === 0 && outgoing.length === 0;

  if (empty) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <HourglassTop sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">No pending requests</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {incoming.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Requests for you ({incoming.length})
          </Typography>
          <Stack spacing={1.5}>
            {incoming.map(r => {
              const name = profiles[r.buddyUserId]?.displayName ?? r.buddyUserId;
              return (
              <Paper key={r.buddyUserId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <BuddyAvatar name={name} size={40} />
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {name}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={loadingId === r.buddyUserId ? <CircularProgress size={14} /> : <Check />}
                      disabled={loadingId === r.buddyUserId}
                      onClick={() => onRespond(r.buddyUserId, true)}
                      sx={{ minHeight: 36 }}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      disabled={loadingId === r.buddyUserId}
                      onClick={() => onRespond(r.buddyUserId, false)}
                      sx={{ minHeight: 36 }}
                    >
                      Decline
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            );})}
          </Stack>
        </Box>
      )}

      {outgoing.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Sent by you ({outgoing.length})
          </Typography>
          <Stack spacing={1.5}>
            {outgoing.map(r => {
              const name = profiles[r.buddyUserId]?.displayName ?? r.buddyUserId;
              return (
              <Paper key={r.buddyUserId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <BuddyAvatar name={name} size={40} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                    <Chip label="Pending" size="small" color="warning" sx={{ mt: 0.25, height: 18, fontSize: '0.65rem' }} />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={loadingId === r.buddyUserId}
                    onClick={() => onCancel(r.buddyUserId)}
                    sx={{ minHeight: 36 }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Paper>
            );})}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

// ── Find Buddies panel ───────────────────────────────────────────────────────

function FindBuddiesPanel({
  onSearch,
  results,
  searching,
  existingBuddyIds,
  onAdd,
  loadingId,
}: {
  onSearch: (q: string) => void;
  results: UserPublicProfile[];
  searching: boolean;
  existingBuddyIds: Set<string>;
  onAdd: (userId: string) => void;
  loadingId: string | null;
}) {
  const [query, setQuery] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onSearch(e.target.value);
  };

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        placeholder="Search by name or email…"
        value={query}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searching ? <CircularProgress size={18} /> : <Search />}
            </InputAdornment>
          ),
        }}
        autoComplete="off"
      />

      {query.length >= 2 && results.length === 0 && !searching && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <PersonSearch sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
        </Box>
      )}

      {results.length === 0 && query.length < 2 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <ScubaDiving sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Type at least 2 characters to search for other divers
          </Typography>
        </Box>
      )}

      {results.length > 0 && (
        <Stack spacing={1.5}>
          {results.map(u => {
            const isBuddy = existingBuddyIds.has(u.userId);
            return (
              <Paper key={u.userId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <BuddyAvatar name={u.displayName} size={40} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.diveCountLabel}
                    </Typography>
                  </Box>
                  {isBuddy ? (
                    <Chip label="Buddy" size="small" color="success" />
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={loadingId === u.userId ? <CircularProgress size={14} /> : <PersonAdd />}
                      disabled={loadingId === u.userId}
                      onClick={() => onAdd(u.userId)}
                      sx={{ minHeight: 36 }}
                    >
                      Add
                    </Button>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const DiveBuddies: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const openProfile = useCallback((userId: string) => navigate(`/u/${userId}`), [navigate]);

  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const {
    buddies, pending, loading, error,
    sendRequest, respond, remove,
    search, searching, searchResults, clearSearch,
  } = useBuddies(userId);

  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  // Dive-share emails link to /buddies?tab=shares, so honour that on first render
  const [searchParams] = useSearchParams();
  const [mobileTab, setMobileTab] = useState(
    searchParams.get('tab') === 'shares' ? 3 : 0
  );
  const [profiles, setProfiles] = useState<Record<string, UserPublicProfile>>({});

  const pendingCount = pending.incoming.length + pending.outgoing.length;

  // Batch-fetch display profiles for all buddies we haven't loaded yet
  useEffect(() => {
    const allIds = [
      ...buddies.map(b => b.buddyUserId),
      ...pending.incoming.map(b => b.buddyUserId),
      ...pending.outgoing.map(b => b.buddyUserId),
    ];
    const missing = Array.from(new Set(allIds)).filter(id => !profiles[id]);
    if (missing.length === 0) return;

    Promise.allSettled(missing.map(id => buddyService.getUserProfile(id))).then(results => {
      const next: Record<string, UserPublicProfile> = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[missing[i]] = r.value;
      });
      if (Object.keys(next).length > 0) {
        setProfiles(prev => ({ ...prev, ...next }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buddies, pending]);

  const handleRemove = useCallback(async (buddyUserId: string) => {
    setActionLoadingId(buddyUserId);
    try {
      await remove(buddyUserId);
      showSuccess('Buddy removed');
    } catch {
      showError('Failed to remove buddy');
    } finally {
      setActionLoadingId(null);
    }
  }, [remove, showSuccess, showError]);

  const handleRespond = useCallback(async (buddyUserId: string, accept: boolean) => {
    setActionLoadingId(buddyUserId);
    try {
      await respond(buddyUserId, accept);
      showSuccess(accept ? 'Buddy request accepted!' : 'Request declined');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to respond to request');
    } finally {
      setActionLoadingId(null);
    }
  }, [respond, showSuccess, showError]);

  // Cancelling deletes both relationship rows so the request can be sent again
  const handleCancel = useCallback(async (buddyUserId: string) => {
    setActionLoadingId(buddyUserId);
    try {
      await remove(buddyUserId);
      showSuccess('Request cancelled');
    } catch {
      showError('Failed to cancel request');
    } finally {
      setActionLoadingId(null);
    }
  }, [remove, showSuccess, showError]);

  const handleAdd = useCallback(async (toUserId: string) => {
    if (!userId) return;
    setActionLoadingId(toUserId);
    try {
      await sendRequest(toUserId);
      clearSearch();
      showSuccess('Buddy request sent!');
    } catch (err: any) {
      showError(err?.message ?? 'Failed to send request');
    } finally {
      setActionLoadingId(null);
    }
  }, [userId, sendRequest, clearSearch, showSuccess, showError]);

  const existingBuddyIds = new Set([
    ...buddies.map(b => b.buddyUserId),
    ...pending.outgoing.map(b => b.buddyUserId),
  ]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="connect with dive buddies"
          description="Find other divers, send buddy requests, and share your scheduled dives — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  const myBuddiesPanel = (
    <MyBuddiesPanel
      buddies={buddies}
      onRemove={handleRemove}
      onOpenProfile={openProfile}
      loadingId={actionLoadingId}
      profiles={profiles}
    />
  );

  const pendingPanel = (
    <PendingPanel
      incoming={pending.incoming}
      outgoing={pending.outgoing}
      onRespond={handleRespond}
      onCancel={handleCancel}
      loadingId={actionLoadingId}
      profiles={profiles}
    />
  );

  const sharedDivesPanel = <IncomingSharesPanel userId={userId} />;

  const filteredSearchResults = Array.from(
    new Map(searchResults.map(u => [u.userId, u])).values()
  ).filter(u => u.userId !== userId && !existingBuddyIds.has(u.userId));

  const findPanel = (
    <FindBuddiesPanel
      onSearch={search}
      results={filteredSearchResults}
      searching={searching}
      existingBuddyIds={existingBuddyIds}
      onAdd={handleAdd}
      loadingId={actionLoadingId}
    />
  );

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dive Buddies
          </Typography>
          <InviteButton />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}

        {!loading && (
          isMobile ? (
            // ── Mobile: stacked tabs ─────────────────────────────────────
            <Box>
              <Tabs
                value={mobileTab}
                onChange={(_, v) => setMobileTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  label={`My Buddies${buddies.length ? ` (${buddies.length})` : ''}`}
                  sx={{ fontWeight: 600, textTransform: 'none', minHeight: 44 }}
                />
                <Tab
                  label={`Pending${pendingCount ? ` (${pendingCount})` : ''}`}
                  sx={{ fontWeight: 600, textTransform: 'none', minHeight: 44 }}
                />
                <Tab
                  label="Find"
                  sx={{ fontWeight: 600, textTransform: 'none', minHeight: 44 }}
                />
                <Tab
                  label="Shared Dives"
                  sx={{ fontWeight: 600, textTransform: 'none', minHeight: 44 }}
                />
              </Tabs>
              {mobileTab === 0 && myBuddiesPanel}
              {mobileTab === 1 && pendingPanel}
              {mobileTab === 2 && findPanel}
              {mobileTab === 3 && sharedDivesPanel}
            </Box>
          ) : (
            // ── Desktop: two-column layout ───────────────────────────────
            <Grid container spacing={3} alignItems="flex-start">
              {/* Left sidebar — My Buddies */}
              <Grid item xs={12} md={5} lg={4}>
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People color="primary" />
                    My Buddies
                    {buddies.length > 0 && (
                      <Chip label={buddies.length} size="small" color="primary" sx={{ ml: 0.5 }} />
                    )}
                  </Typography>
                  {myBuddiesPanel}
                </Paper>
              </Grid>

              {/* Right main — Shared Dives + Find + Pending */}
              <Grid item xs={12} md={7} lg={8}>
                <Stack spacing={3}>
                  {/* Top of the column: an incoming share is an action waiting on the
                      viewer, so it outranks browsing for new buddies. */}
                  <Paper elevation={2} sx={{ p: 3 }} id="shared-dives">
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScubaDiving color="primary" />
                      Shared Dives
                    </Typography>
                    {sharedDivesPanel}
                  </Paper>

                  <Paper elevation={2} sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonSearch color="primary" />
                      Find Buddies
                    </Typography>
                    {findPanel}
                  </Paper>

                  {pendingCount > 0 && (
                    <Paper elevation={2} sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HourglassTop color="warning" />
                        Pending Requests
                        <Chip label={pendingCount} size="small" color="warning" sx={{ ml: 0.5 }} />
                      </Typography>
                      {pendingPanel}
                    </Paper>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )
        )}
      </Box>
    </Box>
  );
};

export default DiveBuddies;
